/**
 * Instant checks while editing, with the same locations the server's /validate uses
 * ("steps.1.with.to"), so each message sits next to its field. The server stays the authority:
 * the editor merges its errors (types of action inputs, unknown keys) with these.
 */
import { t } from '@/lib/i18n';
import { allSteps, type Condition, type FlowDef, STEP_ID_RE, stepLoc } from './definition';
import { type InputField, missingRequired } from './schemaForm';

export interface Issue {
  loc: string;
  message: string;
}

function conditionIssues(conds: Condition[], base: string): Issue[] {
  const out: Issue[] = [];
  conds.forEach((c, i) => {
    if (!c.field.trim()) out.push({ loc: `${base}.${i}.field`, message: t('Choose a field') });
    const empty = c.value === null || c.value === undefined || c.value === '' || (Array.isArray(c.value) && !c.value.length);
    if (c.op !== 'exists' && empty) out.push({ loc: `${base}.${i}.value`, message: t('Enter a value') });
  });
  return out;
}

export function validateFlow(def: FlowDef, name: string, actionFields: (key: string) => InputField[] | null): Issue[] {
  const out: Issue[] = [];
  if (name.trim().length < 2) out.push({ loc: 'name', message: t('Give the automation a name') });
  if (!def.trigger.type) out.push({ loc: 'trigger.type', message: t('Choose what starts the automation') });
  out.push(...conditionIssues(def.trigger.where, 'trigger.where'));
  out.push(...conditionIssues(def.conditions, 'conditions'));
  if (def.target.scope !== 'all' && !def.target.ids.length) out.push({ loc: 'target.ids', message: t('Choose at least one') });

  const steps = allSteps(def);
  if (!steps.length) out.push({ loc: def.phases.length ? 'phases' : 'steps', message: t('Add at least one step') });
  const ids = new Set<string>();
  const seen = new Set<string>();
  steps.forEach((s) => (seen.has(s.id) ? ids.add(s.id) : seen.add(s.id)));
  for (const s of steps) {
    const loc = stepLoc(def, s.id) ?? 'steps';
    if (!STEP_ID_RE.test(s.id)) out.push({ loc: `${loc}.id`, message: t('Use lowercase letters, digits and _ (start with a letter, at most 40)') });
    else if (ids.has(s.id)) out.push({ loc: `${loc}.id`, message: t('Another step has this name') });
    switch (s.type) {
      case 'action': {
        if (!s.action) {
          out.push({ loc: `${loc}.action`, message: t('Choose an action') });
          break;
        }
        const fields = actionFields(s.action);
        if (fields) for (const k of missingRequired(fields, s.with ?? {})) out.push({ loc: `${loc}.with.${k}`, message: t('Required') });
        if (s.action === 'comms.notify') {
          const to = s.with?.to;
          const list = Array.isArray(to) ? to.map(String) : typeof to === 'string' ? [to] : [];
          const bad = list.find((r) => {
            const [kind, ...rest] = r.split(':');
            const id = rest.join(':').trim();
            return !id || (kind === 'email' && !/^[^@\s]+@[^@\s]+$/.test(id));
          });
          if (bad !== undefined) out.push({ loc: `${loc}.with.to`, message: t('Complete every recipient') });
          if (!s.with?.text && !s.with?.template) out.push({ loc: `${loc}.with.text`, message: t('Write the message or choose a template') });
        }
        break;
      }
      case 'wait':
        if (!s.event) out.push({ loc: `${loc}.event`, message: t('Choose the event to wait for') });
        out.push(...conditionIssues(s.match ?? [], `${loc}.match`));
        if (s.on_timeout === 'goto' && !s.timeout_goto) out.push({ loc: `${loc}.timeout_goto`, message: t('Choose the step to go to') });
        break;
      case 'delay':
        if (!s.seconds || s.seconds <= 0) out.push({ loc: `${loc}.seconds`, message: t('Enter how long to wait') });
        break;
      case 'approval':
      case 'task':
        if (s.type === 'approval' && !s.permission && !s.assignee) out.push({ loc: `${loc}.permission`, message: t('Choose who approves') });
        if (s.assignee?.startsWith('user:') && s.assignee.length <= 5) out.push({ loc: `${loc}.assignee`, message: t('Choose a person') });
        break;
      case 'ai':
        if (!s.prompt?.trim()) out.push({ loc: `${loc}.prompt`, message: t('Tell the assistant what to do') });
        break;
      case 'branch':
        out.push(...conditionIssues(s.if ?? [], `${loc}.if`));
        if (!s.if?.length) out.push({ loc: `${loc}.if`, message: t('Add a condition') });
        if (!s.then && !s.else) out.push({ loc: `${loc}.then`, message: t('Choose where to go') });
        break;
      default:
        break;
    }
    for (const [k, ref] of [
      ['then', s.then],
      ['else', s.else],
      ['timeout_goto', s.timeout_goto],
    ] as const) {
      if (ref && !seen.has(ref)) out.push({ loc: `${loc}.${k}`, message: t('This step no longer exists') });
    }
  }
  if (def.cooldown.seconds < 0) out.push({ loc: 'cooldown.seconds', message: t('Enter a positive time') });
  return out;
}

/** Issues at `loc` or below it (a card shows those of its section). */
export function issuesAt(issues: Issue[], loc: string): Issue[] {
  return issues.filter((i) => i.loc === loc || i.loc.startsWith(`${loc}.`));
}

export function firstAt(issues: Issue[], loc: string): string | undefined {
  return issues.find((i) => i.loc === loc)?.message;
}

/** Merge client and server issues, one message per location. */
export function mergeIssues(a: Issue[], b: Issue[]): Issue[] {
  const byLoc = new Map<string, Issue>();
  for (const i of [...a, ...b]) if (!byLoc.has(i.loc)) byLoc.set(i.loc, i);
  return [...byLoc.values()];
}
